import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { FileUp, Upload, CheckCircle2, HelpCircle, Printer } from "lucide-react";

export default function UpLoadPage() {
  const { user } = useAuth();

  const [pharmacyCode, setPharmacyCode] = useState(null);
  const [firma, setFirma] = useState("LEMON");

  const [skladFile, setSkladFile] = useState(null);
  const [pohybyFile, setPohybyFile] = useState(null);
  const [kartyFile, setKartyFile] = useState(null);
  const [odpisyFile, setOdpisyFile] = useState(null);

  const [uploadResult, setUploadResult] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [meta, setMeta] = useState(null);
  const [progress, setProgress] = useState(null);
  const [showOdpisy, setShowOdpisy] = useState(false);

  // když se načte user → nastavíme pobočku & firmu
  useEffect(() => {
    if (user) {
      setPharmacyCode(user.pharmacyCode || null);
      setFirma(user.firma || "LEMON");
    }
  }, [user]);

  // načteme meta.json
  useEffect(() => {
    if (!firma || !pharmacyCode) return;

    (async () => {
      try {
        const res = await fetch(`/api/upload/${firma}/${pharmacyCode}/meta`, {
          credentials: "include",
        });

        if (res.ok) {
          const data = await res.json();
          setMeta(data);

          /// rozhodneme, zda zobrazit odpisy
          if (!data.odpisyLastBatch) {
            setShowOdpisy(true);
          } else {
            const last = new Date(data.odpisyLastBatch);
            const now = new Date();

            if (
              last.getFullYear() !== now.getFullYear() ||
              last.getMonth() !== now.getMonth()
            ) {
              setShowOdpisy(true);
            } else {
              setShowOdpisy(false);
            }
          }
        }
      } catch (e) {
        console.error("Nelze načíst metadata exportu:", e);
      }
    })();
  }, [firma, pharmacyCode]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!skladFile || !pohybyFile || !kartyFile) {
      return alert("Vyber všechny tři povinné soubory.");
    }
    if (!pharmacyCode) {
      return alert("Chybí pharmacyCode – zkuste se znovu přihlásit.");
    }

    const formData = new FormData();
    formData.append("sklad", skladFile);
    formData.append("pohyby", pohybyFile);
    formData.append("karty", kartyFile);
    if (showOdpisy && odpisyFile) formData.append("odpisy", odpisyFile);

    setIsUploading(true);
    setUploadResult(null);
    setProgress(null);

    try {
      const res = await fetch(`/api/upload/${firma}/${pharmacyCode}`, {
        method: "POST",
        body: formData,
        credentials: "include",
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "Chyba při nahrávání");

      setUploadResult(data);
      setMeta(data);
      if (data.progress) setProgress(data.progress);
    } catch (err) {
      alert("Chyba: " + err.message);
    } finally {
      setIsUploading(false);
    }
  };

  const handlePrintInstructions = () => {
    const today = new Date();

    let startYear = today.getFullYear();
    let startMonth = today.getMonth() - 6;

    if (startMonth < 0) {
      startMonth += 12;
      startYear -= 1;
    }

    const todayDay = today.getDate();
    const maxDayInStartMonth = new Date(startYear, startMonth + 1, 0).getDate();
    const safeStartDay = Math.min(todayDay, maxDayInStartMonth);

    const startDate = new Date(startYear, startMonth, safeStartDay);

    const formatDate = (d) => {
      const day = d.getDate();
      const month = d.getMonth() + 1;
      const year = d.getFullYear();
      return `${day}.${month}.${year}`;
    };

    const rangeText = `${formatDate(startDate)} až ${formatDate(today)}`;

    const printHtml = `
      <!doctype html>
      <html lang="cs">
        <head>
          <meta charset="utf-8" />
          <title>Instrukce pro export z Lekisu</title>
          <style>
            @page {
              size: A4 portrait;
              margin: 14mm;
            }

            html, body {
              margin: 0;
              padding: 0;
              background: #fff;
              color: #111827;
              font-family: Arial, Helvetica, sans-serif;
              line-height: 1.45;
              font-size: 12.5px;
            }

            .page {
              padding: 0;
            }

            h1 {
              margin: 0 0 16px 0;
              font-size: 20px;
              text-align: center;
            }

            h2 {
              margin: 18px 0 6px 0;
              font-size: 14px;
            }

            p {
              margin: 0;
              white-space: pre-wrap;
            }
          </style>
        </head>
        <body>
          <div class="page">
            <h1>Instrukce pro export souborů z Lekisu</h1>

            <h2>1. sklad.xlsx</h2>
            <p>SKLAD - SKLADOVÉ KARTY -  ÚPRAVY SK - PŘEHLED POLOŽEK- do filtru množství zadat &gt;0 - kliknout na jakýkoliv řádek v tabulce - úplně vlevo nahoře na liště SOUBOR - EXPORT DO EXCELU - otevře se Excel, v tomto dokumentu kliknout na SOUBOR - ULOŽIT JAKO - a uložit třeba na plochu a pojmenovat sklad.xlsx a ULOŽIT</p>

            <h2>2. pohyby.xlsx</h2>
            <p>AGENDA - STATISTIKY...-VÝDEJE - nahoře nastavit období posledních 6 měsíců, tedy ${rangeText} - dole zaškrtnout navíc Příprava na sklad a Příprava IPL... - dole ZOBRAZIT - otevře se Excel, v tomto dokumentu kliknout na SOUBOR - ULOŽIT JAKO - a uložit třeba na plochu a pojmenovat pohyby.xlsx a ULOŽIT</p>

            <h2>3. karty.xlsx</h2>
            <p>SKLAD - SKLADOVÉ KARTY - nahoře zaškrtnout Nulové karty - kliknout na jakýkoliv řádek v tabulce - úplně vlevo nahoře na liště SOUBOR - EXPORT DO EXCELU - otevře se Excel, v tomto dokumentu kliknout na SOUBOR - ULOŽIT JAKO - a uložit třeba na plochu a pojmenovat karty.xlsx a ULOŽIT</p>

            <h2>4. odpisy.xlsx</h2>
            <p>VÝDEJ - ODPISY - vybrat poslední odpis prošlých expirací - kliknout na jakýkoliv řádek v tabulce - úplně vlevo nahoře na liště SOUBOR - EXPORT DO EXCELU - otevře se Excel, v tomto dokumentu kliknout na SOUBOR - ULOŽIT JAKO - a uložit třeba na plochu a pojmenovat odpisy.xlsx a ULOŽIT</p>
          </div>

          <script>
            window.onafterprint = function () {
              window.close();
            };

            window.onload = function () {
              window.focus();
              setTimeout(function () {
                window.print();
              }, 150);
            };
          </script>
        </body>
      </html>
    `;

    const printWindow = window.open("", "_blank", "width=900,height=700");

    if (!printWindow) {
      alert("Nepodařilo se otevřít okno pro tisk. Zkontrolujte, zda není blokováno prohlížečem.");
      return;
    }

    printWindow.document.open();
    printWindow.document.write(printHtml);
    printWindow.document.close();
  };

  const FileInput = ({ label, file, onChange, helpText }) => (
    <div className="flex items-center gap-2">
      <label
        className={`flex-1 flex items-center justify-between gap-2 px-4 py-2 rounded-lg border cursor-pointer transition-colors ${
          file
            ? "bg-green-50 border-green-400 text-green-700"
            : "bg-gray-50 border-gray-300 text-gray-700 hover:bg-gray-100"
        }`}
      >
        <div className="flex items-center gap-2 truncate">
          {file ? (
            <>
              <CheckCircle2 className="w-4 h-4 text-green-600" />
              <span className="truncate">{file.name}</span>
            </>
          ) : (
            <>
              <Upload className="w-4 h-4" />
              <span>{label}</span>
            </>
          )}
        </div>
        <input type="file" accept=".xlsx" onChange={onChange} className="hidden" />
      </label>

      <div className="relative group">
        <HelpCircle className="w-5 h-5 text-gray-500 cursor-pointer" />
        <div className="absolute left-6 top-1/2 -translate-y-1/2 w-72 bg-white text-gray-700 text-sm border rounded-lg shadow-md opacity-0 group-hover:opacity-100 transition-opacity p-3 z-10">
          {helpText}
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-xl font-semibold flex items-center gap-2">
        <FileUp className="w-6 h-6" /> Nahrát exporty
      </h1>

      {meta ? (
        meta.lastBatch ? (
          <div className="text-sm text-gray-600">
            Poslední export:{" "}
            {new Date(meta.lastBatch).toLocaleString("cs-CZ", {
              day: "numeric",
              month: "numeric",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
              hour12: false,
              timeZone: "Europe/Prague",
            }).replace(/\. /g, ".")}
          </div>
        ) : (
          <div className="text-sm text-rose-600 font-medium">
            Zatím nebyl nahrán žádný export
          </div>
        )
      ) : null}

      <form onSubmit={handleSubmit} className="space-y-4 max-w-md">
        <FileInput
          label="Vybrat soubor – sklad.xlsx"
          file={skladFile}
          onChange={(e) => setSkladFile(e.target.files[0])}
          helpText="SKLAD - SKLADOVÉ KARTY -  ÚPRAVY SK - PŘEHLED POLOŽEK- do filtru množství zadat >0 - kliknout na jakýkoliv řádek v tabulce - úplně vlevo nahoře na liště SOUBOR - EXPORT DO EXCELU - otevře se Excel, v tomto dokumentu kliknout na SOUBOR - ULOŽIT JAKO - a uložit třeba na plochu a pojmenovat sklad.xlsx a ULOŽIT"
        />
        <FileInput
          label="Vybrat soubor – pohyby.xlsx"
          file={pohybyFile}
          onChange={(e) => setPohybyFile(e.target.files[0])}
          helpText="AGENDA - STATISTIKY...-VÝDEJE - nahoře nastavit období posledních 6 měsíců, tedy 16.3.2025 až 16.9.2025 - dole zaškrtnout navíc Příprava na sklad a Příprava IPL... - dole ZOBRAZIT - otevře se Excel, v tomto dokumentu kliknout na SOUBOR - ULOŽIT JAKO - a uložit třeba na plochu a pojmenovat pohyby.xlsx a ULOŽIT"
        />
        <FileInput
          label="Vybrat soubor – karty.xlsx"
          file={kartyFile}
          onChange={(e) => setKartyFile(e.target.files[0])}
          helpText="SKLAD - SKLADOVÉ KARTY - nahoře zaškrtnout Nulové karty - kliknout na jakýkoliv řádek v tabulce - úplně vlevo nahoře na liště SOUBOR - EXPORT DO EXCELU - otevře se Excel, v tomto dokumentu kliknout na SOUBOR - ULOŽIT JAKO - a uložit třeba na plochu a pojmenovat karty.xlsx a ULOŽIT"
        />

        {showOdpisy && (
          <FileInput
            label="Vybrat soubor – odpisy.xlsx"
            file={odpisyFile}
            onChange={(e) => setOdpisyFile(e.target.files[0])}
            helpText="VÝDEJ - ODPISY - vybrat poslední odpis prošlých expirací - kliknout na jakýkoliv řádek v tabulce - úplně vlevo nahoře na liště SOUBOR - EXPORT DO EXCELU - otevře se Excel, v tomto dokumentu kliknout na SOUBOR - ULOŽIT JAKO - a uložit třeba na plochu a pojmenovat odpisy.xlsx a ULOŽIT"
          />
        )}

        <button
          type="submit"
          disabled={isUploading}
          className="w-full mt-4 px-4 py-4 bg-green-700 hover:bg-green-800 text-white font-medium rounded-lg transition-colors"
        >
          {isUploading ? "Nahrávám…" : "Nahrát exporty"}
        </button>

        <div className="pt-12">
          <div className="max-w-md rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <span>Chcete kompletní instrukce pro export z Lekisu?</span>

              <button
                type="button"
                onClick={handlePrintInstructions}
                className="inline-flex items-center justify-center gap-2 rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
              >
                <Printer className="h-4 w-4" />
                Tisknout
              </button>
            </div>
          </div>
        </div>
      </form>

      {progress && progress.total > 0 && (
        <div className="mt-6">
          <div className="w-full bg-gray-200 rounded h-4 overflow-hidden">
            <div
              className="h-4 bg-green-600"
              style={{
                width: `${Math.round(
                  (progress.processed / progress.total) * 100
                )}%`,
              }}
            />
          </div>
          <p className="text-sm text-gray-600 mt-1">
            Zpracováno {progress.processed} / {progress.total}
          </p>
        </div>
      )}

      {uploadResult?.message && (
        <div className="text-sm text-green-700 bg-green-100 border border-green-300 rounded p-3">
          {uploadResult.message}{" "}
          {uploadResult.counts && (
            <span>
              ({uploadResult.counts.sklad} sklad, {uploadResult.counts.pohyby} pohybů,{" "}
              {uploadResult.counts.karty} karet, {uploadResult.counts.odpisy} odpisů)
            </span>
          )}
        </div>
      )}
    </div>
  );
}